<template>
  <div>
    <section class="bg-primary-500 text-white">
      <div class="container px-0 xl:px-0">
        <div class="flex flex-wrap flex-col-reverse md:flex-row items-stretch">
          <div class="relative z-10 w-full md:w-1/2 pt-50 md:pt-175 lg:pt-225 pb-50 md:pb-100 lg:pb-150 pl-15 lg:pl-50 xl:pl-100">
            <breadcrumb
              light
              class="mb-50"
              :breadcrumbs="[data?.entry].filter((b) => b)"
            />
            <h1
              v-if="data?.entry?.heading"
              class="wysiwyg-title text-4xl lg:text-5xl xl:text-6xl md:-ml-150 md:translate-x-150"
              v-html="data.entry.heading"
            />
            <div
              v-if="data?.entry?.intro"
              class="mt-25 md:pr-50 lg:pr-100 text-lg wysiwyg"
              v-html="data.entry.intro"
            />
            <NuxtLink
              v-if="data?.entry?.button.url"
              :to="data.entry.button.url"
              :target="data.entry.button.target"
              :aria-label="data.entry.button.arialabel"
              class="inline-flex items-center text-xl font-serif underline space-x-5 group mt-15 transition-colors hover:text-primary-100 text-white"
            >
              <span>{{ data.entry.button.text }}</span>
              <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
            </NuxtLink>
          </div>
          <div class="w-full md:w-1/2 relative">
            <Media
              v-if="data?.entry?.image.length"
              :media="data.entry.image[0]"
              sizes="(max-width: 600px) 100px, 100vw"
              :alt="data.entry?.title"
              width="743"
              height="1149"
              class="md:absolute md:inset-0 md:w-full md:h-full md:object-cover"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="mt-100">
      <div class="container px-0 xl:px-25 text-center pt-20">
        <h2
          v-if="data?.entry?.heading2"
          class="wysiwyg-title text-3xl md:text-4xl text-secondary"
          v-html="data.entry.heading2"
        />
        <div class="scrollbar overflow-x-auto w-full pb-20 text-left">
          <ul class="flex flex-nowrap mt-20 text-xl text-white font-bold">
            <li
              v-if="data?.blogOverview"
              class="flex-1 min-w-[300px] bg-secondary"
            >
              <NuxtLink
                :to="`/${data.blogOverview.uri}`"
                class="relative group block size-full"
              >
                <h4 class="absolute z-10 bottom-0 left-0 m-20 group-hover:underline">
                  {{ data.blogOverview.title }}
                </h4>
                <div class="relative overflow-hidden aspect-[495/568]">
                  <Media
                    v-if="data.blogOverview.image?.length"
                    :media="data.blogOverview.image[0]"
                    :alt="data.blogOverview.title"
                    class="w-full group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </NuxtLink>
            </li>
            <li
              v-if="data?.interiorViewersOverview"
              class="flex-1 min-w-[300px] bg-secondary"
            >
              <NuxtLink
                :to="`/${data.interiorViewersOverview.uri}`"
                class="relative group block size-full"
              >
                <h4 class="absolute z-10 bottom-0 left-0 m-20 group-hover:underline">
                  {{ data.interiorViewersOverview.title }}
                </h4>
                <div class="relative overflow-hidden aspect-[495/568]">
                  <Media
                    v-if="data.interiorViewersOverview.image?.length"
                    :media="data.interiorViewersOverview.image[0]"
                    :alt="data.interiorViewersOverview.title"
                    class="w-full group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </NuxtLink>
            </li>
            <li
              v-if="data?.interiorStylesOverview"
              class="flex-1 min-w-[300px] bg-secondary"
            >
              <NuxtLink
                :to="`/${data.interiorStylesOverview.uri}`"
                class="relative group block size-full"
              >
                <h4 class="absolute z-10 bottom-0 left-0 m-20 group-hover:underline">
                  {{ data.interiorStylesOverview.title }}
                </h4>
                <div class="relative overflow-hidden aspect-[495/568]">
                  <Media
                    v-if="data.interiorStylesOverview.image?.length"
                    :media="data.interiorStylesOverview.image[0]"
                    :alt="data.interiorStylesOverview.title"
                    class="w-full group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section
      v-if="data?.entry?.inspirationInstagramImages?.length"
      class="mb-100"
    >
      <div class="container">
        <h3
          v-if="data?.entry?.heading3"
          class="wysiwyg-title text-3xl md:text-4xl mt-100 text-secondary"
          v-html="data.entry.heading3"
        />
        <div
          v-if="data?.entry?.intro3"
          class="mt-25 max-w-md wysiwyg"
          v-html="data.entry.intro3"
        />

        <div class="flex items-end space-x-30">
          <div
            v-if="data?.entry?.inspirationInstagramImages?.length > 0"
            class="w-1/3"
          >
            <NuxtLink
              :to="`https://www.instagram.com/p/${data?.entry?.inspirationInstagramImages[0]?.title}`"
              target="_blank"
              aria-label="instagram"
            >
              <img
                :src="data.entry.inspirationInstagramImages[0].url"
                class="w-full mt-30"
                alt=""
                loading="lazy"
              >
            </NuxtLink>
          </div>
          <div
            v-if="data?.entry?.inspirationInstagramImages?.length > 1"
            class="w-1/4"
          >
            <NuxtLink
              :to="`https://www.instagram.com/p/${data.entry.inspirationInstagramImages[1].title}`"
              target="_blank"
              aria-label="instagram"
            >
              <img
                :src="data.entry.inspirationInstagramImages[1].url"
                class="w-full mt-30"
                alt=""
                loading="lazy"
              >
            </NuxtLink>
          </div>
          <div
            v-if="data?.entry?.inspirationInstagramImages?.length > 2"
            class="w-1/6 self-start"
          >
            <NuxtLink
              :to="`https://www.instagram.com/p/${data.entry.inspirationInstagramImages[2].title}`"
              target="_blank"
              aria-label="instagram"
            >
              <img
                :src="data.entry.inspirationInstagramImages[2].url"
                class="w-full mt-30"
                alt=""
                loading="lazy"
              >
            </NuxtLink>
          </div>
          <div
            v-if="data?.entry?.inspirationInstagramImages?.length > 3"
            class="w-1/4"
          >
            <NuxtLink
              :to="`https://www.instagram.com/p/${data.entry.inspirationInstagramImages[3].title}`"
              target="_blank"
              aria-label="instagram"
            >
              <img
                :src="data.entry.inspirationInstagramImages[3].url"
                class="w-full mt-30"
                alt=""
                loading="lazy"
              >
            </NuxtLink>
          </div>
        </div>
        <div class="flex items-end justify-center space-x-30">
          <div class="w-1/3" />
          <div
            v-if="data?.entry?.inspirationInstagramImages?.length > 4"
            class="w-1/4"
          >
            <NuxtLink
              :to="`https://www.instagram.com/p/${data.entry.inspirationInstagramImages[4].title}`"
              target="_blank"
              aria-label="instagram"
            >
              <img
                :src="data.entry.inspirationInstagramImages[4].url"
                class="w-full mt-30"
                alt=""
                loading="lazy"
              >
            </NuxtLink>
          </div>
          <div
            v-if="data?.entry?.inspirationInstagramImages?.length > 5"
            class="w-1/6 self-start"
          >
            <NuxtLink
              :to="`https://www.instagram.com/p/${data.entry.inspirationInstagramImages[5].title}`"
              target="_blank"
              aria-label="instagram"
            >
              <img
                :src="data.entry.inspirationInstagramImages[5].url"
                class="w-full mt-30"
                alt=""
                loading="lazy"
              >
            </NuxtLink>
          </div>
          <div class="w-1/4" />
        </div>
      </div>
    </section>

    <section
      v-if="data?.inspirationEntries?.length"
      class="relative w-full"
    >
      <Media
        v-if="data?.inspirationEntries[0].heroFsImage?.length"
        :media="data.inspirationEntries[0].heroFsImage[0]"
        sizes="(max-width: 600px) 100px, (max-width: 1024px) 500px, 100vw"
        :alt="data.inspirationEntries[0].title"
        width="1920"
        height="1250"
        class="absolute inset-0 w-full h-full object-cover"
      />
      <div class="relative pt-[50vh] pb-100 lg:pb-[200px] w-full z-10">
        <div class="container max-w-screen-xl">
          <div class="flex flex-wrap justify-between items-end">
            <div class="w-full lg:w-2/3 mb-50 lg:mb-0 lg:pr-100">
              <h3
                v-if="data?.inspirationEntries[0]?.heading"
                class="wysiwyg-title text-5xl md:text-6xl xl:text-7xl text-white"
                v-html="data.inspirationEntries[0].heading"
              />
              <div
                v-if="data?.inspirationEntries[0]?.intro"
                class="text-lg font-medium text-white mt-50 max-w-lg wysiwyg"
                v-html="data.inspirationEntries[0].intro"
              />
              <NuxtLink
                v-if="data?.inspirationEntries[0]?.uri"
                :to="`/${data.inspirationEntries[0].uri}`"
                class="inline-flex items-center text-2xl font-serif underline space-x-5 group text-white mt-25"
              >
                <span>{{ $t('inspiration.cta') }}</span>
                <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
              </NuxtLink>
            </div>
            <div class="w-full lg:w-1/3">
              <ul>
                <li
                  v-for="product in data?.inspirationEntries[0]?.inspirationFeaturedProducts"
                  :key="`product-${product.id}`"
                >
                  <NuxtLink
                    :to="`/${product.uri}`"
                    class="flex group mt-10"
                  >
                    <div class="bg-primary-100 w-[130px]">
                      <img
                        v-if="product.productImages?.length"
                        :src="product.productImages[0].url"
                        class="mix-blend-darken"
                        loading="lazy"
                        :alt="product.title"
                      >
                    </div>

                    <div class="flex flex-1 p-20 ml-1 bg-primary-100 text-base items-center group-hover:underline transition">
                      <p>
                        <b>{{ product.title }}</b><br>
                        <product-price :product="product" />
                      </p>
                    </div>
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <content-blocks :content-blocks="data?.entry?.contentBlocks" />

    <section class="my-100">
      <div class="container">
        <div class="pr-60">
          <h2
            v-if="data?.entry?.heading4"
            class="wysiwyg-title text-3xl md:text-4xl mr-20 text-secondary"
            v-html="data.entry.heading4"
          />
          <NuxtLink
            v-if="data?.global?.instagram"
            :to="data.global.instagram"
            target="_blank"
            rel="norefferer noopener"
            class="inline-flex mt-10 items-center text-xl font-serif underline space-x-5 group"
          >
            <span>{{ $t('socials.follow-us') }} instagram</span>
            <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
          </NuxtLink>
        </div>
      </div>

      <Swiper
        :slides-per-view="'auto'"
        :navigation="true"
        :modules="[SwiperNavigation]"
        class="flex flex-nowrap"
      >
        <SwiperSlide
          v-for="{ id, productInstagram } in data?.entry?.productFeaturedInstagramProducts"
          :key="`instagram-product-${id}`"
          class="flex flex-shrink-0 mt-25 px-15"
        >
          <product-instagram :instagram-products="productInstagram" />
        </SwiperSlide>
      </Swiper>
    </section>

    <section
      v-if="data?.inspirationEntries?.length > 1"
      class="my-100"
    >
      <div class="text-white container px-0 xl:px-25">
        <div class="md:grid md:grid-cols-2 md:grid-rows-[auto,auto] bg-primary-700">
          <div class="md:col-start-2 md:row-start-1 md:row-span-1 px-25 md:px-50 lg:px-100 md:pt-50 lg:pt-100 md:self-end">
            <h3
              v-if="data.inspirationEntries[1].heading"
              class="wysiwyg-title text-3xl p-25 pt-75 z-10 text-center mb-[-75px] relative sm:text-4xl sm:mb-[-87px] md:p-0 md:pt-0 md:mb-0 md:text-left md:text-5xl xl:text-6xl 2xl:text-7xl"
              v-html="data.inspirationEntries[1].heading"
            />
          </div>
          <div class="relative w-full md:col-start-1 md:row-start-1 md:row-span-2">
            <Media
              v-if="data?.inspirationEntries[1]?.heroImage?.length"
              :media="data.inspirationEntries[1].heroImage[0]"
              sizes="(max-width: 600px) 200px, (max-width: 1024px) 400px, 100vw"
              :alt="data.inspirationEntries[1].title"
              width="743"
              height="923"
              class="w-full"
              loading="lazy"
            />
          </div>
          <div class="md:col-start-2 md:row-start-2 md:row-span-1 px-25 md:px-50 lg:px-100 pb-25 md:pb-50 lg:pb-100">
            <div
              v-if="data?.inspirationEntries[1]?.intro"
              class="mt-25 wysiwyg"
              v-html="data.inspirationEntries[1].intro"
            />
            <NuxtLink
              v-if="data?.inspirationEntries[1]?.uri"
              :to="`/${data.inspirationEntries[1].uri}`"
              class="inline-flex items-center text-2xl font-serif underline space-x-5 group mt-25"
            >
              <span>{{ $t('inspiration.cta') }}</span>
              <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <blog-posts
      :data="data"
      :variables="variables"
      :offset="offset"
      hide-title
      @update:offset="offset = $event"
    />

    <reviews />
  </div>
</template>

<script setup>
import {
  useI18n,
  useAsyncGql,
  useSeo,
  watch,
  definePageMeta,
  SwiperNavigation,
  ref,
} from '#imports';

// General icons
import chevronIcon from '@/assets/img/icon-chevron.svg';

// Data
const { locale } = useI18n({ useScope: 'global' });
const offset = ref(0);
const variables = ref({
  site: locale.value,
  limit: 6,
});
const { data } = await useAsyncGql('inspirationOverview', {
  ...variables.value,
  offset: offset.value,
});

// Watcher
watch(data, (value) => {
  if (value?.entry?.seomatic) {
    useSeo(value.entry.seomatic);
  }
}, { immediate: true });

// Meta
definePageMeta({
  headerImage: true,
});
</script>
